import type { PrimeStatusString } from './types/user';

export type AdobeAnalyticsProfile = {
  school?: string | null;
  schoolID?: number | null;
  cohortSchool?: string | null;
  cohortSchoolID?: number | null;
};

export type AdobeAnalyticsUser = {
  userID?: number | null;
  hasPaid?: boolean | null;
  prime?: {
    hasPaid?: boolean | null;
    statusString?: PrimeStatusString | string | null;
  } | null;
  profile?: AdobeAnalyticsProfile | null;
};

export type AdobeVisitorIdentifiers = {
  accountId?: string;
  accountName?: string;
  userId?: string;
};

type VisitorIdentifierOptions = {
  includeUserId?: boolean;
};

const formatAdobeIdentifier = (
  value?: string | number | null,
): string | undefined => {
  const normalizedValue = String(value ?? '').trim();
  if (!normalizedValue) return undefined;

  return normalizedValue.startsWith('ae:')
    ? normalizedValue
    : `ae:${normalizedValue}`;
};

const formatAdobeNumericIdentifier = (
  value?: number | null,
): string | undefined => {
  if (!value || value <= 0) return undefined;

  return formatAdobeIdentifier(value);
};

const getAdobeAccountIdentifiers = (
  profile?: AdobeAnalyticsProfile | null,
): Pick<AdobeVisitorIdentifiers, 'accountId' | 'accountName'> => {
  const cohortAccountId = formatAdobeNumericIdentifier(profile?.cohortSchoolID);
  const cohortAccountName = formatAdobeIdentifier(profile?.cohortSchool);

  if (cohortAccountId && cohortAccountName) {
    return {
      accountId: cohortAccountId,
      accountName: cohortAccountName,
    };
  }

  const schoolAccountId = formatAdobeNumericIdentifier(profile?.schoolID);
  const schoolAccountName = formatAdobeIdentifier(profile?.school);

  return {
    ...(schoolAccountId && schoolAccountName
      ? { accountId: schoolAccountId, accountName: schoolAccountName }
      : {}),
  };
};

export const getAdobeVisitorIdentifiers = (
  user?: AdobeAnalyticsUser | null,
  { includeUserId = true }: VisitorIdentifierOptions = {},
): AdobeVisitorIdentifiers => {
  const account = getAdobeAccountIdentifiers(user?.profile);
  const userId = includeUserId
    ? formatAdobeNumericIdentifier(user?.userID)
    : undefined;

  return {
    ...account,
    ...(userId ? { userId } : {}),
  };
};

/**
 * Builds the reduced user payload needed for Adobe visitor fields.
 */
export const getAdobeAnalyticsUser = (
  user?: AdobeAnalyticsUser | null,
): AdobeAnalyticsUser | undefined => {
  if (!user) return undefined;

  return {
    userID: user.userID,
    hasPaid: user.hasPaid,
    prime: user.prime
      ? {
          hasPaid: user.prime.hasPaid,
          statusString: user.prime.statusString,
        }
      : undefined,
    profile: user.profile
      ? {
          school: user.profile.school,
          schoolID: user.profile.schoolID,
          cohortSchool: user.profile.cohortSchool,
          cohortSchoolID: user.profile.cohortSchoolID,
        }
      : undefined,
  };
};
